/**
 * L4-JUICE-005: Screen Shake Manager
 *
 * Manages camera shake effects for impactful events.
 * Uses sine wave with exponential decay for smooth, natural shake.
 */

import * as PIXI from 'pixi.js';

/**
 * Screen shake intensity levels
 */
export type ShakeIntensity = 'light' | 'medium' | 'heavy';

/**
 * Shake configuration
 */
interface ShakeConfig {
  displacement: number; // Pixels (±)
  duration: number;     // Milliseconds
}

/**
 * ScreenShakeManager
 *
 * Manages camera shake effects for impactful events.
 * Uses sine wave with exponential decay for smooth, natural shake.
 */
export class ScreenShakeManager {
  private stage: PIXI.Container;
  private isShaking: boolean = false;
  private currentShakeId: number | null = null;

  private static readonly CONFIG: Record<ShakeIntensity, ShakeConfig> = {
    light: { displacement: 2, duration: 100 },   // ±2px, 100ms
    medium: { displacement: 4, duration: 150 },  // ±4px, 150ms
    heavy: { displacement: 8, duration: 200 }    // ±8px, 200ms
  };

  private static readonly FREQUENCY = 30; // Oscillations per second

  constructor(stage: PIXI.Container) {
    this.stage = stage;
  }

  /**
   * Trigger screen shake effect
   * If shake already active, replaces with new shake if more intense
   *
   * @param intensity - Shake strength
   */
  public shake(intensity: ShakeIntensity): void {
    // Cancel existing shake if less intense
    if (this.isShaking) {
      this.cancelShake();
    }

    const config = ScreenShakeManager.CONFIG[intensity];
    this.applyShake(config.displacement, config.duration);
  }

  /**
   * Get configuration for intensity level
   * (Exposed for testing)
   */
  public getConfig(intensity: ShakeIntensity): ShakeConfig {
    return ScreenShakeManager.CONFIG[intensity];
  }

  /**
   * Check if currently shaking
   */
  public isActive(): boolean {
    return this.isShaking;
  }

  /**
   * Cancel current shake and reset stage
   */
  public cancelShake(): void {
    if (this.currentShakeId !== null) {
      cancelAnimationFrame(this.currentShakeId);
      this.currentShakeId = null;
    }
    this.isShaking = false;
    this.reset();
  }

  /**
   * Apply sine wave shake with exponential decay
   */
  private applyShake(displacement: number, duration: number): void {
    const startTime = Date.now();
    this.isShaking = true;

    const update = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= duration) {
        this.reset();
        this.isShaking = false;
        this.currentShakeId = null;
        return;
      }

      // Calculate progress (0 to 1)
      const progress = elapsed / duration;

      // Exponential decay: amplitude reduces over time
      const amplitude = displacement * (1 - progress);

      // Sine wave oscillation
      const x = Math.sin(elapsed * ScreenShakeManager.FREQUENCY * 0.1) * amplitude;
      const y = Math.cos(elapsed * ScreenShakeManager.FREQUENCY * 0.1) * amplitude;

      // Apply displacement to stage
      this.stage.x = x;
      this.stage.y = y;

      // Schedule next frame
      this.currentShakeId = requestAnimationFrame(update);
    };

    update();
  }

  /**
   * Reset stage position to origin
   */
  private reset(): void {
    if (!this.stage) return;
    this.stage.x = 0;
    this.stage.y = 0;
  }

  /**
   * Destroy manager and cleanup
   */
  public destroy(): void {
    this.cancelShake();
  }
}
